import Button from "@material-ui/core/Button";
import DialogContentText from "@material-ui/core/DialogContentText";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState,
  PageInfo
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import CollectionListPage from "../../components/CollectionListPage/CollectionListPage";
import {
  useCollectionBulkDelete,
  useCollectionBulkPublish
} from "../../mutations";
import { useCollectionListQuery } from "../../queries";
import {
  collectionAddNationwideUrl,
  collectionAddUrl,
  collectionListUrl,
  CollectionListUrlDialog,
  CollectionListUrlQueryParams,
  collectionUrl
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import { getSortQueryVariables } from "./sort";

interface CollectionListProps {
  params: CollectionListUrlQueryParams;
}

const useStyles = makeStyles(
  {
    btnDisabled: {
      "& span": {
        color: "#b3b3b3"
      }
    },
    errMessage: {
      background: "#ff4b3e",
      borderRadius: "10px",
      color: "#fff",
      margin: "10px 20px",
      padding: "8px 10px",
      position: "absolute",
      right: 0,
      top: "90px",
      zIndex: 9
    }
  },
  { name: "CollectionList" }
);

export const CollectionList: React.FC<CollectionListProps> = ({ params }) => {
  const classes = useStyles(params);
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const shop = useShop();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const { updateListSettings, settings } = useListSettings(
    ListViews.COLLECTION_LIST
  );
  const intl = useIntl();

  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sort: getSortQueryVariables(params)
    }),
    [params]
  );
  const { data, loading, refetch } = useCollectionListQuery({
    displayLoader: true,
    variables: queryVariables
  });

  useEffect(
    () =>
      navigate(
        collectionListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  const [
    collectionBulkDelete,
    collectionBulkDeleteOpts
  ] = useCollectionBulkDelete({
    onCompleted: data => {
      if (data?.collectionBulkDelete?.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.successCollectionDeletion)
        });
        refetch();
        reset();
        closeModal();
      }
    }
  });

  const [samePublished, setSamePublished] = useState(false);
  const [initSelection, setInitSelection] = useState(false);

  const [
    collectionBulkPublish,
    collectionBulkPublishOpts
  ] = useCollectionBulkPublish({
    onCompleted: data => {
      if (data?.collectionBulkPublish?.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        refetch();
        reset();
        closeModal();
      }
    }
  });

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : Number(params.activeTab);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: params =>
      collectionListUrl(params as CollectionListUrlQueryParams),
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    CollectionListUrlDialog,
    CollectionListUrlQueryParams
  >(navigate, collectionListUrl, params);

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      collectionListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(collectionListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data?.collections?.pageInfo) as PageInfo,
    paginationState,
    params
  );

  const handleSort = createSortHandler(navigate, collectionListUrl, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");

  const compareCollections = (collections, compareTo) => {
    for (let i = 1; i < collections.length; i++) {
      switch (compareTo) {
        case "published":
          if (collections[i].isPublish !== collections[0].isPublish) {
            return false;
          }
          break;
      }
    }
    return true;
  };

  useEffect(() => {
    let formattedCollections = [];
    const newCollections =
      data && data.collections
        ? data.collections.edges.filter(edge =>
            listElements.includes(edge.node.id)
          )
        : [];
    formattedCollections = newCollections.map(collection =>
      collection.node
        ? {
            id: collection.node.id,
            isPublish: collection.node.isPublished
          }
        : ""
    );
    setInitSelection(
      formattedCollections &&
        formattedCollections[0] &&
        formattedCollections[0].isPublish
    );
    setSamePublished(compareCollections(formattedCollections, "published"));
  }, [listElements]);

  const collections = maybe(() => data?.collections?.edges, []).map(
    edge => edge.node
  );

  return (
    <>
      {!samePublished ? (
        <div className={classes.errMessage}>
          <strong>Note:</strong> Different status selected. Please check
          selected item/s to proceed with action.
        </div>
      ) : (
        ""
      )}

      <CollectionListPage
        brands={null}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={getFilterOpts(params)}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onAdd={() => navigate(collectionAddUrl)}
        onAddNationwide={() => navigate(collectionAddNationwideUrl)}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs?.map(tab => tab.name) || []}
        disabled={loading}
        collections={collections}
        settings={settings}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onSort={handleSort}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        sort={getSortParams(params)}
        onRowClick={id => () => navigate(collectionUrl(id))}
        toolbar={
          <>
            <Button
              color="primary"
              className={
                !initSelection || !samePublished ? classes.btnDisabled : ""
              }
              disabled={!initSelection || !samePublished}
              onClick={() =>
                openModal("unpublish", {
                  ids: listElements
                })
              }
            >
              <FormattedMessage
                defaultMessage="Unpublish"
                description="unpublish collections"
              />
            </Button>
            <Button
              color="primary"
              onClick={() =>
                openModal("publish", {
                  ids: listElements
                })
              }
              className={
                initSelection || !samePublished ? classes.btnDisabled : ""
              }
              disabled={initSelection || !samePublished}
            >
              <FormattedMessage
                defaultMessage="Publish"
                description="publish collections"
              />
            </Button>
            <IconButton
              color="primary"
              onClick={() =>
                openModal("remove", {
                  ids: listElements
                })
              }
            >
              <DeleteIcon />
            </IconButton>
          </>
        }
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
      />
      <ActionDialog
        open={params.action === "publish" && maybe(() => params.ids.length > 0)}
        onClose={closeModal}
        confirmButtonState={collectionBulkPublishOpts.status}
        onConfirm={() =>
          collectionBulkPublish({
            variables: {
              ids: params.ids,
              isPublished: true
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Published Collections",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to publish this collection?} other{Are you sure you want to publish {displayQuantity} collections?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={
          params.action === "unpublish" && maybe(() => params.ids.length > 0)
        }
        onClose={closeModal}
        confirmButtonState={collectionBulkPublishOpts.status}
        onConfirm={() =>
          collectionBulkPublish({
            variables: {
              ids: params.ids,
              isPublished: false
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Unpublished Collections",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to unpublish this collection?} other{Are you sure you want to unpublish {displayQuantity} collections?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "remove" && maybe(() => params.ids.length > 0)}
        onClose={closeModal}
        confirmButtonState={collectionBulkDeleteOpts.status}
        onConfirm={() =>
          collectionBulkDelete({
            variables: {
              ids: params.ids
            }
          })
        }
        variant="delete"
        title={intl.formatMessage({
          defaultMessage: "Delete Collection",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this collection?} other{Are you sure you want to delete {displayQuantity} collections?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
    </>
  );
};
export default CollectionList;
